import apiV2 from '../api/v2';
import type { ChatType } from './chat';

/** Which thread a row describes. Same discriminator the chat endpoints take. */
export type ConversationStatus = ChatType;

/**
 * Identity of an inbox row. A row is the pair (patientId, status) - the same
 * patient appears once per thread type, so patientId alone is not unique.
 */
export interface ConversationRowRef {
    patientId: string;
    status: ConversationStatus;
}

export const conversationRowKey = (row: ConversationRowRef): string =>
    `${row.status}:${row.patientId}`;

export type ConversationPriority = 'normal' | 'high' | 'extreme';
export type ConversationFrustration = 'low' | 'medium' | 'high';

// Internal rows are set by staff only, so their AI markers are always false.
export interface ConversationTriage {
    priority: ConversationPriority | null;
    frustration: ConversationFrustration | null;
    priorityByAi: boolean;
    frustrationByAi: boolean;
}

export interface ConversationTriagePatch {
    priority?: ConversationPriority;
    frustration?: ConversationFrustration;
}

export type ConversationSortBy = 'name' | 'date' | 'priority' | 'frustration';

export interface ConversationRow extends ConversationRowRef, ConversationTriage {
    name: string;
    profileThumbnailUrl: string | null;
    practiceId: string | null;
    practiceName: string | null;
    /** "Dr. {first} {last}" from the patient's own practice row, or null. */
    practiceDoctor: string | null;
    team: string | null;
    teamRoleId: string | null;
    assignedTo: string | null;
    assignedToName: string | null;
    unread: boolean;
    lastMessageText: string | null;
    lastMessageDate: string | null;
    reviewedBy: string | null;
}

export interface ConversationsPageMeta {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

export interface ConversationsPageResponse {
    data: ConversationRow[];
    meta: ConversationsPageMeta;
}

export interface ConversationsParams {
    filter: 'unread' | 'seen' | 'all';
    page: number;
    limit: number;
    sort: ConversationSortBy;
    order: 'asc' | 'desc';
    team?: string[];
    assignee?: string[];
    // Exact practice ids - names aren't unique, so the filter is id-based.
    practiceIds?: string[];
    status?: ConversationStatus[];
    search?: string;
}

// The assignee filter's "nobody" token.
export const UNASSIGNED_CONVERSATION_FILTER = 'unassigned';

export interface FilterOptions {
    teams: string[];
    // No practice fields: practice options come from GET /v2/practices/options
    // (usePracticeOptions) - id-keyed, so duplicate names can't collapse.
}

export const fetchConversationsPage = async (
    params: ConversationsParams
): Promise<ConversationsPageResponse> => {
    // Filter values are raw names that may contain a comma, so they stay arrays
    // - apiV2 serializes them as bracketed params.
    const query: Record<string, string | string[]> = {
        filter: params.filter,
        page: String(params.page),
        limit: String(params.limit),
        sort: params.sort,
        order: params.order
    };
    if (params.team?.length) query.team = params.team;
    if (params.assignee?.length) query.assignee = params.assignee;
    if (params.practiceIds?.length) query.practiceIds = params.practiceIds;
    if (params.status?.length) query.status = params.status;
    if (params.search?.trim()) query.search = params.search.trim();

    const response = await apiV2.get('/conversations', { params: query });
    return response.data;
};

export const fetchConversation = async (
    patientId: string,
    status: ConversationStatus
): Promise<ConversationRow> => {
    const response = await apiV2.get(`/conversations/${encodeURIComponent(patientId)}`, {
        params: { status }
    });
    return response.data.data;
};

export const patchConversationTriage = async (
    row: ConversationRowRef,
    patch: ConversationTriagePatch
): Promise<ConversationTriage> => {
    const response = await apiV2.patch(
        `/conversations/${encodeURIComponent(row.patientId)}/triage`,
        { chatType: row.status, ...patch }
    );
    return response.data.data;
};

export const fetchFilterOptions = async (): Promise<FilterOptions> => {
    const response = await apiV2.get('/conversations/filters');
    return response.data.data;
};
